import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {PasswordVerifyModel} from '@core/api/model/passwordVerifyModel';


import { Observable } from 'rxjs';

import {
  CodeRequestModel,
  CodeVerifyModel,
  PhoneBodyModel,
  ResponseModel,
  SessionTokenModel,
  UserBaseModel,
  UserRegistrationModel
} from '..';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';
import { FileModel } from '../model/fileModel';
import { UserImageModel } from '../model/userImageModel';
import { UserImageResponseModel } from '../model/userImageResponseModel';
import { TransportModel } from '@core/api/model/transportModel';
import { OperatorModel } from '@core/api/model/operatorModel';
import { UserMeModel } from '@core/api';
import { UserSession } from '@core/api/model/userSession';
import {SocialNetworkModel} from '@core/api/model/socialNetworkModel';
import {SocialNetworkVerifyModel} from '@core/api/model/socialNetworkVerifyModel';
import {UserApplyTerms} from "@core/api/model/userApplyTerms";


@Injectable()
export class UserService {

  public configuration = new Configuration();
  public defaultHeaders = new HttpHeaders();
  protected basePath = 'https://localhost/v1';

  constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   *
   * Authenticates user. Verifies 2FA code and creates user session token
   * @param body
   */
  public userLogin2faVerifyPost(body: CodeVerifyModel): Observable<ResponseModel<SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<SessionTokenModel>>(
      `${ this.basePath }/user/login/2fa-verify`,
      body
    );
  }

  /**
   *
   * Authenticates user. Verifies user by password and create user session token
   * @param body
   */
  public userLoginByPasswordPost(body: PasswordVerifyModel): Observable<ResponseModel<SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<SessionTokenModel>>(
        `${ this.basePath }/user/login/password`,
        body
    );
  }

  /**
   *
   * Authenticate user. Check user phone number, create 2fa code and send it to the user.
   * @param body
   */
  public userLoginPost(body: PhoneBodyModel): Observable<ResponseModel<CodeRequestModel>> {
    return this.httpClient.post<ResponseModel<CodeRequestModel>>(
      `${ this.basePath }/user/login`,
      body
    );
  }

  /**
   *
   * Logout action
   */
  public userLogoutPost(): Observable<ResponseModel<null>> {
    return this.httpClient.post<ResponseModel<null>>(`${ this.basePath }/user/logout`, null);
  }

  /**
   *
   * Checks user session token and returns information about current user.
   */
  public userMeGet(): Observable<ResponseModel<UserBaseModel>> {
    return this.httpClient.get<ResponseModel<UserBaseModel>>(`${ this.basePath }/user/me`);
  }

  public updateExtendSession(): Observable<ResponseModel<UserSession>> {
    return this.httpClient.put<ResponseModel<UserSession>>(`${ this.basePath }/user/extend-session`, {});
  }

  public userSocialNetworksGet(): Observable<ResponseModel<SocialNetworkModel[]>> {
    return this.httpClient.get<ResponseModel<SocialNetworkModel[]>>(`${ this.basePath }/user/social-networks`);
  }

  public userSocialNetworksPost(body: SocialNetworkModel): Observable<ResponseModel<SocialNetworkModel>> {
    return this.httpClient.post<ResponseModel<any>>(`${ this.basePath }/user/link-network`, body);
  }

  public userLoginSocialNetworkVerifyPost(body: SocialNetworkVerifyModel): Observable<ResponseModel<SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<SessionTokenModel>>(
        `${ this.basePath }/user/login/network`,
        body
    );
  }

  /**
   *
   * Request 2fa code for registarion operation
   * @param body
   */
  public userRegistration2faCodePost(body: PhoneBodyModel): Observable<ResponseModel<CodeRequestModel>> {
    return this.httpClient.post<ResponseModel<CodeRequestModel>>(
      `${ this.basePath }/user/registration/2fa-code`,
      body
    );
  }

  /**
   *
   * Request 2fa code for recovery password operation
   * @param body
   */
  public userRecoveryPassword2faCodePost(body: PhoneBodyModel): Observable<ResponseModel<CodeRequestModel>> {
    return this.httpClient.post<ResponseModel<CodeRequestModel>>(
        `${ this.basePath }/user/recovery-password/2fa-code`,
        body
    );
  }

  /**
   *
   * Verify 2fa code for registarion operation and creates temporary token for registration
   * @param body
   */
  public userRegistration2faVerifyPost(body: CodeVerifyModel): Observable<ResponseModel<SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<SessionTokenModel>>(
      `${ this.basePath }/user/registration/2fa-verify`,
      body
    );
  }

  /**
   *
   * Verify 2fa code for recovery password operation and creates temporary token
   * @param body
   */
  public userRecoveryPassword2faVerifyPost(body: CodeVerifyModel): Observable<ResponseModel<SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<SessionTokenModel>>(
        `${ this.basePath }/user/recovery-password/2fa-verify`,
        body
    );
  }

  /**
   *
   * User registration action
   * @param body
   */
  public userRegistrationPost(body: UserRegistrationModel): Observable<ResponseModel<UserBaseModel, SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<UserBaseModel>>(
      `${ this.basePath }/user/registration`,
      body
    );
  }

  /**
   *
   * User recovery password action
   * @param body
   */
  public userRecoveryPasswordPost(body: UserRegistrationModel): Observable<ResponseModel<UserBaseModel, SessionTokenModel>> {
    return this.httpClient.post<ResponseModel<UserBaseModel>>(
        `${ this.basePath }/user/recovery-password`,
        body
    );
  }

  /**
   *
   * Add image to user
   * @param id Numeric ID of the user
   * @param body
   */
  public userIdImagePost(id: number, body?: UserImageModel): Observable<ResponseModel<UserImageResponseModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdImagePost.');
    }

    const formParams = new FormData();

    formParams.append(`type`, body.type.toString());
    formParams.append(`image`, body.image);

    return this.httpClient.post<ResponseModel<UserImageResponseModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/image`, formParams);
  }

  /**
   *
   * Add image to user
   * @param id Numeric ID of the user
   * @param body
   */
  public userIdApplyTermsPost(id: number, body?: UserApplyTerms): Observable<ResponseModel<UserBaseModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdApplyTerms.');
    }

    return this.httpClient.post<ResponseModel<UserBaseModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/apply-terms`, body);
  }

  /**
   *
   * Add image to user
   * @param id Numeric ID of the user
   * @param trailerId
   * @param body
   */
  public userIdTransportIdImagePost(id: number, trailerId: number, body?: UserImageModel): Observable<ResponseModel<UserImageResponseModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdTransportIdImagePost.');
    }

    if (trailerId === null || trailerId === undefined) {
      throw new Error('Required parameter trailerId was null or undefined when calling userIdTransportIdImagePost.');
    }

    const formParams = new FormData();

    formParams.append(`type`, body.type.toString());
    formParams.append(`image`, body.image);

    return this.httpClient.post<ResponseModel<UserImageResponseModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport/${encodeURIComponent(String(trailerId))}/image`, formParams);
  }

  /**
   *
   * Returns a list images of user
   * @param id Numeric ID of the user
   */
  public usersIdImagesGet(id: number): Observable<ResponseModel<UserImageResponseModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling usersIdImagesGet.');
    }

    return this.httpClient.get<ResponseModel<UserImageResponseModel[]>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/image`);
  }

  /**
   *
   * Returns a list transport
   * @param id Numeric ID of the transport
   */
  public usersIdTransportGet(id: number): Observable<ResponseModel<TransportModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling usersIdTransportGet.');
    }

    return this.httpClient.get<ResponseModel<TransportModel[]>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport`);
  }

  /**
   *
   * Returns a transport
   * @param id
   * @param transportId
   */
  public usersIdTransportIdGet(id: number, transportId: number): Observable<ResponseModel<TransportModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling usersIdTransportIdGet.');
    }

    if (transportId === null || transportId === undefined) {
      throw new Error('Required parameter id was null or undefined when calling usersIdTransportIdGet.');
    }

    return this.httpClient.get<ResponseModel<TransportModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport/${encodeURIComponent(String(transportId))}`);
  }

  /**
   *
   * Returns a list of users
   */
  public userGet<T = UserBaseModel>(body): Observable<ResponseModel<T[]>> {

    return this.httpClient.get<ResponseModel<T[]>>(`${this.basePath}/user`,
      {
        params: body
      });
  }

  /**
   *
   * Delete a user
   * @param user
   */
  public userPost<T = UserBaseModel>(user: T): Observable<ResponseModel<T>> {

    if (user === null || user === undefined) {
      throw new Error('Required parameter user was null or undefined when calling userDelete.');
    }

    return this.httpClient.post<ResponseModel<T>>(`${this.basePath}/user`, user);
  }

  /**
   *
   * Delete a user
   * @param id Get user
   */
  public userIdDelete(id: number): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdDelete.');
    }

    return this.httpClient.delete<ResponseModel<null>>(`${this.basePath}/user/${encodeURIComponent(String(id))}`);
  }

  /**
   *
   * Returns a user
   * @param id Get user
   */
  public userIdGet<T = UserBaseModel>(id: number): Observable<ResponseModel<T>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdGet.');
    }

    return this.httpClient.get<ResponseModel<T>>(`${this.basePath}/user/${encodeURIComponent(String(id))}`);
  }

  /**
   *
   * Returns a list of users
   * @param id Get user
   * @param user
   */
  public userIdPut<T = UserBaseModel>(id: number, user: T): Observable<ResponseModel<T>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdPut.');
    }

    return this.httpClient.put<ResponseModel<T>>(`${this.basePath}/user/${encodeURIComponent(String(id))}`,
      user
    );
  }

  /**
   *
   * Add transport to user
   * @param id Get user
   * @param transport
   */
  public userIdTransportPost(id: number, transport: TransportModel): Observable<ResponseModel<TransportModel>> {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdTransportPost.');
    }

    return this.httpClient.post<ResponseModel<TransportModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport`,
      transport
    );
  }

  /**
   *
   * Add transport to user
   * @param id Get user
   * @param transportId
   * @param transport
   */
  public userIdTransportPut(id: number, transportId: number, transport: TransportModel): Observable<ResponseModel<TransportModel>> {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdTransportPost.');
    }

    return this.httpClient.put<ResponseModel<TransportModel>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport/${encodeURIComponent(String(transportId))}`,
      transport
    );
  }

  /**
   *
   * Delete a user
   * @param id Get user
   * @param transportId
   */
  public userIdTransportDelete(id: number, transportId: number): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userIdTransportDelete.');
    }

    return this.httpClient.delete<ResponseModel<null>>(`${this.basePath}/user/${encodeURIComponent(String(id))}/transport/${encodeURIComponent(String(transportId))}`);
  }

  public getUserConfirmationCode(): Observable<any> {
    return this.httpClient.get<any>(`${this.basePath}/user/confirmations`);
  }

  public confirmUserConfirmationCode(code): Observable<any> {
    return this.httpClient.post(`${this.basePath}/user/confirmations`, {
      code: code
    });
  }
}
